// Публичные страницы сайта: заголовок и описание для выдачи, картинка превью ссылки и главная
// картинка страницы. Один список: из него собираются готовые страницы, карта сайта, проверка
// сборки и заголовок вкладки при переходах (изменение seo, design.md «Заголовки и описания»).
// Слова — по частотам Вордстата 04.11.2026; «ДнД» в витрине миниатюры — решение Ивана 04.11.
import type { Catalog } from "../api/types";
import { formatRub, lowestProductPrice } from "./prices";

export const SITE_NAME = "Селфигурка";
const TITLE_SUFFIX = ` — ${SITE_NAME}`;

// Превью ссылок 1200×630 — из примеров работ витрины (design/web_images.py).
export const OG_IMAGE_GIFT = "/og/gift_shelf.jpg";
export const OG_IMAGE_MINI = "/og/mini_dice.jpg";

// Описание оболочки приложения и закрытых экранов — как в index.html.
export const SITE_DESCRIPTION = "Фигурка с твоим лицом: загрузи селфи, выбери образ — покажем эскиз фигурки.";

// Что нужно о документах: из списка документов или из текста одного документа.
export type DocumentsForMeta = { documents: { id: string; title: string; about: string; revision: string }[] };

export type PageMeta = {
  path: string;
  title: string;
  description: string;
  ogImage: string;
  // Документ — для карты сайта: дата редакции вместо даты сборки.
  revision: string | null;
};

function withPrice(text: string, price: number | null): string {
  return price === null ? "" : `${text}${formatRub(price)}`;
}

function sentence(parts: string[]): string {
  return parts.filter((part) => part !== "").join(", ") + ".";
}

function readableDate(revision: string): string {
  const [year, month, day] = revision.split("-");
  return `${day}.${month}.${year}`;
}

// Страницы без документов: тексты постоянные, цены — из каталога этой версии сервера.
function sitePages(catalog: Catalog | undefined): PageMeta[] {
  const giftFile = catalog === undefined ? null : lowestProductPrice(catalog, "gift", "file");
  const miniFile = catalog === undefined ? null : lowestProductPrice(catalog, "miniature", "file");
  const miniPrint = catalog === undefined ? null : lowestProductPrice(catalog, "miniature", "print_grey");
  return [
    {
      path: "/",
      title: `Фигурка и статуэтка по фото на заказ${TITLE_SUFFIX}`,
      description:
        "Фигурка с твоим лицом по селфи: миниатюра 32 мм для настольных игр или статуэтка в подарок 10 и 15 см. " +
        "Эскиз — бесплатно.",
      ogImage: OG_IMAGE_GIFT,
      revision: null,
    },
    {
      path: "/gift",
      title: `Статуэтка по фото в подарок, 10 и 15 см${TITLE_SUFFIX}`,
      description:
        "Фигурка человека по фото в любом образе: опиши героя своими словами. " +
        sentence(["Серая статуэтка 10 или 15 см", "эскиз бесплатно", withPrice("файл модели — ", giftFile)]),
      ogImage: OG_IMAGE_GIFT,
      revision: null,
    },
    {
      path: "/mini",
      title: `Миниатюра ДнД по фото для настольных игр, 32 мм${TITLE_SUFFIX}`,
      description:
        "Миниатюра ДнД 32 мм с твоим лицом: выбери мир, расу и класс. " +
        sentence([
          "Эскиз бесплатно",
          withPrice("файл STL для своего принтера — ", miniFile),
          withPrice("печать — ", miniPrint),
        ]),
      ogImage: OG_IMAGE_MINI,
      revision: null,
    },
    {
      path: "/how",
      title: `Как сделать фигурку по фото${TITLE_SUFFIX}`,
      description:
        "Селфи, образ, бесплатный эскиз, 3D-модель с гарантией возврата и печать: как из фото получается фигурка.",
      ogImage: OG_IMAGE_GIFT,
      revision: null,
    },
    {
      path: "/faq",
      title: `Вопросы о фигурках по фото${TITLE_SUFFIX}`,
      description:
        "Сколько стоит попробовать, будет ли фигурка похожа, можно ли известного героя и из чего печатаем.",
      ogImage: OG_IMAGE_GIFT,
      revision: null,
    },
    {
      path: "/docs",
      title: `Документы${TITLE_SUFFIX}`,
      description:
        "Оферта, условия возврата, политика обработки данных, согласие на обработку фото и пользовательское соглашение.",
      ogImage: OG_IMAGE_GIFT,
      revision: null,
    },
  ];
}

function documentPages(documents: DocumentsForMeta | undefined): PageMeta[] {
  return (documents?.documents ?? []).map((document) => ({
    path: `/docs/${document.id}`,
    title: `${document.title}${TITLE_SUFFIX}`,
    description: `${document.about}. Редакция от ${readableDate(document.revision)}.`,
    ogImage: OG_IMAGE_GIFT,
    revision: document.revision,
  }));
}

// Все публичные страницы: без документов, пока их список не известен.
export function publicPages(catalog: Catalog | undefined, documents: DocumentsForMeta | undefined): PageMeta[] {
  return [...sitePages(catalog), ...documentPages(documents)];
}

// Мета страницы по адресу; null — закрытый экран (заказ, вход, учётка) или неизвестный адрес.
export function pageMetaFor(
  pathname: string,
  catalog: Catalog | undefined,
  documents: DocumentsForMeta | undefined,
): PageMeta | null {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return publicPages(catalog, documents).find((page) => page.path === normalized) ?? null;
}

// Канонический адрес страницы: основной домен, без слэша на конце (у главной — сам домен со слэшем).
export function canonicalUrl(siteUrl: string, path: string): string {
  const origin = siteUrl.replace(/\/+$/, "");
  return path === "/" ? `${origin}/` : `${origin}${path}`;
}
